// The chase camera's look-around as a free orbit: a rotation in the ship's own
// frame that a drag turns about the drawn camera's own axes. It has no up of
// its own, so no angle is a pole it has to stop at or flip over.
import type * as THREE from 'three';

/** Axis times radians of a unit quaternion, the short way round. */
export function rotationVector(q: THREE.Quaternion, out: THREE.Vector3): THREE.Vector3 {
  const sign = q.w < 0 ? -1 : 1;
  const sinHalf = Math.hypot(q.x, q.y, q.z);
  const k = sinHalf < 1e-9 ? 2 * sign : (sign * 2 * Math.atan2(sinHalf, Math.abs(q.w))) / sinHalf;
  return out.set(q.x * k, q.y * k, q.z * k);
}

/**
 * Bank a drag of `dx`, `dy` screen pixels into `pending`, in the look's own
 * axes: across turns about the drawn camera's up and down about its right, with
 * the signs the yaw and pitch it replaces had. `roll` is how far the drawn
 * camera sits turned about its forward off the look.
 */
export function addDrag(
  pending: THREE.Vector3, dx: number, dy: number, radPerPx: number, roll = 0,
): THREE.Vector3 {
  const aboutRight = dy * radPerPx;
  const aboutUp = dx * radPerPx;
  const c = Math.cos(roll);
  const s = Math.sin(roll);
  return pending.set(
    pending.x + aboutRight * c - aboutUp * s,
    pending.y + aboutRight * s + aboutUp * c,
    pending.z);
}

/** Bank a turn of `rad` about the ship's own up, in the look's own axes. */
export function addShipUpTurn(pending: THREE.Vector3, look: THREE.Quaternion, rad: number): THREE.Vector3 {
  // The ship's +y carried into the look's axes, which is the look's inverse applied to it.
  const { x, y, z, w } = look;
  return pending.set(
    pending.x + (2 * w * z + 2 * x * y) * rad,
    pending.y + (1 - 2 * x * x - 2 * z * z) * rad,
    pending.z + (2 * y * z - 2 * w * x) * rad);
}

/** Set `pending` to the shortest turn that brings `look` back onto the nose. */
export function backToNose(pending: THREE.Vector3, look: THREE.Quaternion): THREE.Vector3 {
  return rotationVector(look, pending).negate();
}

/** Radians `look` sits off the nose. */
export function lookAngle(look: { x: number; y: number; z: number; w: number }): number {
  return 2 * Math.atan2(Math.hypot(look.x, look.y, look.z), Math.abs(look.w));
}

/**
 * Turn `look` through `fraction` of `pending`, about the look's own axes, and
 * take that much off `pending`; a remainder under `snapRad` is turned at once.
 * `pending` is a vector rather than a target so a fast drag that runs more
 * than half a turn ahead of the drawn view still goes the way it was dragged.
 */
export function easeLook(
  look: THREE.Quaternion, pending: THREE.Vector3, fraction: number, snapRad: number,
): void {
  const left = pending.length();
  if (left === 0) return;
  const f = left * (1 - fraction) < snapRad ? 1 : fraction;
  const angle = left * f;
  const k = Math.sin(angle / 2) / left;
  const ex = pending.x * k;
  const ey = pending.y * k;
  const ez = pending.z * k;
  const ew = Math.cos(angle / 2);
  const { x, y, z, w } = look;
  look.set(
    w * ex + x * ew + y * ez - z * ey,
    w * ey - x * ez + y * ew + z * ex,
    w * ez + x * ey - y * ex + z * ew,
    w * ew - x * ex - y * ey - z * ez).normalize();
  if (f === 1) pending.set(0, 0, 0);
  else pending.multiplyScalar(1 - f);
}
